/**
 * AtlasFishingModule — FISHING (2026-09-24). The pure reel and cast rules are in lib/fishing.ts; the server rolls
 * the fish from the water, the hour, the weather and the biome (reducers/field_guide.ts castLine) and judges the
 * timing (reelIn).
 *
 *   CAST     facing water within 20 m (the sea, a lake, a river), on foot: E — the line goes out, a bobber rides the
 *            surface.
 *   BITE     3–12 s later the bobber dives and the REEL starts: hold E to lift the catch
 *            zone (the mouse stays the weapon's — a click is a shot); keep the darting fish inside it until the meter
 *            fills. Let the meter run dry and it gets away.
 *            E before the bite reels in an empty line.
 *   LANDED   the fish — its generated model, drawn at its true length — is held up in front of you for a moment; it
 *            goes into your stacks (a `brine_catch` stack by species, the longest landed kept as LEN) and into the
 *            Field Guide.
 * Models: public/props/fish/<species>.glb (Hunyuan3D-2.1, cooked to ~3k triangles; LOOKED AT for facing).
 * Levers: `__fishing()` (state; `.cast()`, `.bite()` → debugFishBite, `.hold(on)`).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Logger } from 'enginex/core/Logger.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { interactFocus } from '../lib/interact_focus.js';
import { waterKindAt, waterLiveAt, waterStillAt, stampWater } from '../lib/water_fx.js';
import { newBobberRings, bobberLanded, bobberRings, type RingStamp } from '../lib/bobber_rings.js';
import { fishSpec, type Water } from '../lib/field_guide_catalog.js';
import { castSearch, newReel, stepReel, fishModelYaw, fishDrawLength, REEL, type ReelState, type CastSpot } from '../lib/fishing.js';
import { sfx } from '../lib/sfx.js';
import { onInteract, interactPrompt, interactKey, typingTarget } from '../lib/interact_input.js';
import { keyIs } from '../lib/keybinds.js';
import { padHeld } from '../lib/gamepad.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { commonsSeedNow } from '../lib/town_names.js';
import { fishingWaterAt, castLevel } from '../lib/fishing_water.js';
import { el } from './town/town_ui.js';

interface CastRow { identity: { toHexString(): string }; water: string; x: number; z: number; species: string; cm: number; castAt: { microsSinceUnixEpoch: bigint }; biteAt: { microsSinceUnixEpoch: bigint } }

type Phase_ = 'idle' | 'casting' | 'waiting' | 'reeling' | 'showing';
/** The server's bite window (reducers/field_guide.ts BITE_WINDOW_US): a reel-in later than this lands nothing. */
const BITE_WINDOW_MS = 25_000;
const FISH_DIR = 'props/fish';
const WALK_AWAY_M = 25;
const SHOW_MS = 3500;

export class AtlasFishingModule implements IModule {
  readonly name = 'AtlasFishing';
  private app!: App;
  private scene!: THREE.Scene;
  private phase: Phase_ = 'idle';
  private spot: CastSpot | null = null;
  private bobber: THREE.Group | null = null;
  private line: THREE.Line | null = null;
  private reel: ReelState | null = null;
  private reelSpecies = '';
  private holding = false;
  private forceHold: boolean | 'track' | null = null;
  private prompt: HTMLDivElement | null = null;
  private reelUi: { root: HTMLDivElement; zone: HTMLDivElement; fish: HTMLDivElement; meter: HTMLDivElement; label: HTMLDivElement } | null = null;
  private focused: CastSpot | null = null;
  private lastT = 0;
  private castSentMs = 0;
  /** Last strike request (ms, performance clock): asked again while the server's clock has not reached the bite. */
  private strikeSentMs = -Infinity;
  /** THE SERVER'S CLOCK, NOT OURS (review 2026-10-05): the bite is timed from when THIS cast's row arrived plus the row's
   *  own biteAt − castAt — comparing Date.now() with a server stamp missed the bite on a skewed clock (and this box's
   *  steps back ~2.5 s every ~32 s). The cast's row is told apart from an older one by its castAt, never by our clock. */
  private prevCastMicros = -1n;
  private castSeenMs = 0;
  private biteAfterMs = 0;
  private shown: THREE.Group | null = null;
  private showUntil = 0;
  private models = new Map<string, THREE.Object3D>();
  private modelLoads = new Map<string, Promise<THREE.Object3D | null>>();
  private lastResult = '';
  /** F2: the bound Interact key's HOLD (the reel) — the press itself comes through `onInteract` (key + pad). */
  private keyDown = false;
  private readonly onBlur = (): void => { this.keyDown = false; };
  private offInteract: (() => void) | null = null;
  private onKey = (e: KeyboardEvent): void => this.hold(e, true);
  private onKeyUp = (e: KeyboardEvent): void => this.hold(e, false);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    app.world.addSystem(makeSystem('Fishing', Phase.UI, 37, () => this.tick()));
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur); // no keyup arrives for a key held across an alt-tab: the reel stayed held
    this.offInteract = onInteract(() => this.press());
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    const p = document.createElement('div');
    p.id = 'fishing-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-info) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    const lever = () => ({
      phase: this.phase, spot: this.spot, reel: this.reel ? { zone: +this.reel.zone.toFixed(2), fish: +this.reel.fish.toFixed(2), progress: +this.reel.progress.toFixed(2), done: this.reel.done } : null,
      species: this.reelSpecies, lastResult: this.lastResult, ringStamps: this.rings.stamps, ringsRung: this.ringsRung, models: [...this.models.keys()], showing: !!this.shown,
    });
    (window as unknown as { __fishing?: unknown }).__fishing = Object.assign(lever, {
      cast: () => { const s = this.findSpot(); if (s) void this.cast(s); return s; },
      bite: () => atlas.conn?.reducers.debugFishBite({}),
      hold: (on: boolean | 'track' | null) => { this.forceHold = on; return lever(); },
      loadModel: (key: string) => this.model(key).then((m) => !!m),
      /** A probe lever: the nearest waterline within `maxM` (16 bearings) and a spot 8 m short of it, facing it. */
      seek: (maxM = 150) => {
        for (let d = 4; d <= maxM; d += 4) {
          for (let k = 0; k < 16; k++) {
            const yaw = (k / 16) * Math.PI * 2;
            const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
            const x = atlas.localX + fx * d, z = atlas.localZ + fz * d;
            if (this.kindAt(x, z)) return { x: x - fx * 8, z: z - fz * 8, yaw, dist: d, water: this.kindAt(x, z) };
          }
        }
        return null;
      },
    });
  }

  private subscribe(): void {
    try { atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM fishing_cast']); } catch { /* pre-publish */ }
  }

  private myCast(): CastRow | null {
    const me = atlas.identityHex;
    try {
      for (const c of (atlas.conn?.db as unknown as { fishingCast?: { iter(): Iterable<CastRow> } })?.fishingCast?.iter() ?? []) {
        if (c.identity.toHexString() === me) return c;
      }
    } catch { /* not subscribed */ }
    return null;
  }

  private canFish(): boolean {
    if (atlas.currentWorldId === null || atlas.spaceFlight || atlas.hoverRide || atlas.flyMode || atlas.editMode || atlas.seated || atlas.interiorId) return false;
    const still = waterStillAt(atlas.localX, atlas.localZ);
    return !(still !== null && still - atlas.localY > 1.0); // wading is fine; swimming is not
  }

  /** The idle prompt's spot, CACHED (2026-09-25): the search walks up to twelve samples along the facing, each the
   *  rendered water lookup plus the analytic terrain for the sea — every frame while on foot. Re-searched when the
   *  player has moved 2 m, turned ~9°, or half a second has passed. */
  private spotCache: { x: number; z: number; yaw: number; at: number; spot: CastSpot | null } | null = null;
  private cachedSpot(now: number): CastSpot | null {
    const c = this.spotCache;
    const turned = c ? Math.abs(Math.atan2(Math.sin(atlas.localYaw - c.yaw), Math.cos(atlas.localYaw - c.yaw))) : 0;
    if (!c || now - c.at > 500 || turned > 0.15 || Math.hypot(atlas.localX - c.x, atlas.localZ - c.z) > 2) {
      this.spotCache = { x: atlas.localX, z: atlas.localZ, yaw: atlas.localYaw, at: now, spot: this.findSpot() };
    }
    return this.spotCache!.spot;
  }

  private findSpot(): CastSpot | null {
    // The rendered water answers first; the SEA falls back to the server's own rule (ground below sea level − 0.3),
    // so a cast never waits on the camera's shoreline bake after a long ride or a teleport.
    return castSearch(atlas.localX, atlas.localZ, atlas.localYaw, (x, z) => this.kindAt(x, z));
  }

  /** The water at a point as the SERVER will judge it (lib/fishing_water: the server's own rule on the server's own
   *  inputs, 2026-09-28 — castLine checks lakes and rivers now, and an offered cast must never be refused). */
  private kindAt(x: number, z: number): Water | null {
    const seed = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? commonsSeedNow() : '';
    return fishingWaterAt(seed, x, z, waterKindAt(x, z) as Water | null, waterStillAt(x, z));
  }

  // ── the loop ────────────────────────────────────────────────────────────────
  private tick(): void {
    const now = performance.now();
    // WALL time, sub-stepped for the reel: the server's window is real seconds after the bite, so a slow renderer
    // must not make the reel run slow (a 0.1 s cap per frame lagged it behind the window at 1 fps).
    const dt = this.lastT ? Math.min(1, (now - this.lastT) / 1000) : 0;
    this.lastT = now;
    this.animateShown(now);
    if (!this.canFish() && this.phase !== 'showing') {
      if (this.phase !== 'idle') this.abandon('');
      this.showPrompt(null);
      return;
    }
    switch (this.phase) {
      case 'idle': {
        const s = this.cachedSpot(now);
        const focus = interactFocus();
        if (s) focus.claim({ id: 'fishing:cast', dist: Math.max(3, s.dist * 0.6), priority: 0, label: interactPrompt(`cast a line (${s.water})`) });
        this.focused = s && focus.owns('fishing:cast') ? s : null;
        this.showPrompt(this.focused ? interactPrompt(`cast a line into the ${this.focused.water}`) : null);
        return;
      }
      case 'casting': {
        // Only THIS cast's row (a refused reel can leave an older row behind for a moment).
        const c = this.myCast();
        if (c && c.castAt.microsSinceUnixEpoch !== this.prevCastMicros) {
          this.phase = 'waiting';
          this.castSeenMs = now;
          this.biteAfterMs = Number((c.biteAt.microsSinceUnixEpoch - c.castAt.microsSinceUnixEpoch) / 1000n);
        }
        else if (now - this.castSentMs > 6000) this.abandon('');
        this.showPrompt(null);
        this.bob(now, false);
        return;
      }
      case 'waiting': {
        const c = this.myCast();
        if (!c) { this.abandon(''); return; }
        if (this.spot && Math.hypot(this.spot.x - atlas.localX, this.spot.z - atlas.localZ) > WALK_AWAY_M) { this.abandon('You walked off — the line came in'); return; }
        // Re-read each tick: the row's biteAt can move (the probe's debugFishBite); the offset stays server-relative.
        this.biteAfterMs = Number((c.biteAt.microsSinceUnixEpoch - c.castAt.microsSinceUnixEpoch) / 1000n);
        const sinceCast = now - this.castSeenMs;
        const bitten = sinceCast >= this.biteAfterMs;
        // The strike never landed inside the server's window (a stalled connection): the fish is gone — say so.
        if (bitten && !c.species && sinceCast > this.biteAfterMs + BITE_WINDOW_MS - 1000) {
          void atlas.conn?.reducers.reelIn({ landed: false }).catch(() => { /* already in */ });
          this.abandon('It got away'); return;
        }
        this.bob(now, bitten);
        this.showPrompt(bitten ? `A bite! Hold ${interactKey()} to reel` : `Waiting for a bite… (${interactKey()} to reel in)`);
        if (bitten) {
          // THE HIDDEN ROLL (sweep 2026-10-05, A4): the server keeps the fish to itself until the bite — a cast row that
          // named it let a player reel in early and recast until a big one showed. Strike to learn what took the bait.
          if (!c.species) { this.strike(now); return; }
          this.startReel(c.species);
        }
        return;
      }
      case 'reeling': {
        const spec = fishSpec(this.reelSpecies);
        let r = this.reel ?? newReel();
        for (let left = dt; left > 1e-6 && !r.done; left -= 1 / 30) {
          // `track` (a probe lever): a skilled hand in the module, judged every sub-step.
          const hold = this.forceHold === 'track' ? r.zone + r.zoneV * 0.3 < r.fish : (this.forceHold ?? (this.keyDown || padHeld('interact'))); // the pad's held interact reels too (F2)
          r = stepReel(r, hold, Math.min(1 / 30, left), spec?.fight ?? 2, Math.random);
        }
        this.reel = r;
        this.bob(now, true);
        this.drawReel();
        this.showPrompt(null);
        if (this.reel.done) void this.finish(this.reel.done === 'landed');
        return;
      }
      case 'showing': return;
    }
  }

  /** The bound key's hold state (a rebind follows; Ctrl/Meta chords and a focused text field never reel). */
  private hold(e: KeyboardEvent, down: boolean): void {
    if (!keyIs(e.code, 'interact') || e.ctrlKey || e.metaKey) return;
    if (down && typingTarget(e.target)) return;
    this.keyDown = down;
  }

  /** One Interact press (bound key or pad): cast from the prompt, or reel the waiting line in. */
  private press(): void {
    if (this.phase === 'reeling') return;
    // The press re-searches once (the prompt's spot may be half a second old); the cached one if the fresh search misses.
    if (this.phase === 'idle' && this.focused && interactFocus().owns('fishing:cast')) { void this.cast(this.findSpot() ?? this.focused); return; }
    if (this.phase === 'waiting') {
      // THE BITE'S ROUND TRIP: since the hidden roll the prompt says "A bite!" while strikeLine is still on the wire and
      // the phase is still 'waiting' — a press then reeled in an empty line and lost the fish. At the bite, a press strikes.
      const now = performance.now();
      if (this.biteAfterMs > 0 && now - this.castSeenMs >= this.biteAfterMs) { this.strike(now); return; }
      void atlas.conn?.reducers.reelIn({ landed: false }).catch(() => { /* already in */ });
      this.abandon('Reeled in — nothing on the line');
    }
  }

  private async cast(s: CastSpot): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    this.phase = 'casting';
    this.spot = s;
    this.castSentMs = performance.now();
    this.prevCastMicros = this.myCast()?.castAt.microsSinceUnixEpoch ?? -1n;
    this.drawBobber(s);
    this.ring(bobberLanded(this.rings, s.x, s.z, performance.now() / 1000)); // the line lands: a ring where it struck (physics follow-up A)
    sfx('sfx_water_splash', { gain: 0.35, rate: 1.4 });
    try {
      // f32 on the wire — the same numbers kindAt judged (lib/fishing_water rounds them the same way).
      const lv = castLevel(waterStillAt(s.x, s.z));
      await conn.reducers.castLine({ water: s.water, x: Math.fround(s.x), z: Math.fround(s.z), level: Number.isFinite(lv) ? lv : 0 });
    } catch (e) {
      this.abandon(String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, ''));
    }
  }

  /** Ask the server what took the bait; it answers by filling the cast row's species (a refused early strike is quiet). */
  private strike(now: number): void {
    if (now - this.strikeSentMs < 400) return;
    this.strikeSentMs = now;
    const r = atlas.conn?.reducers as unknown as { strikeLine?: (a: Record<string, never>) => Promise<void> } | undefined;
    if (typeof r?.strikeLine === 'function') r.strikeLine({}).catch(() => { /* the server answers on its own clock */ });
  }

  private startReel(species: string): void {
    this.phase = 'reeling';
    this.reelSpecies = species;
    this.reel = newReel();
    this.holding = false;
    this.keyDown = false; // a key still down from the cast must be pressed afresh (an auto-repeat re-arms it)
    sfx('sfx_water_splash', { gain: 0.55, rate: 1.1 });
    void this.model(species); // warm the landing model
  }

  private async finish(landed: boolean): Promise<void> {
    const conn = atlas.conn;
    const species = this.reelSpecies;
    this.phase = 'showing';
    this.hideReel();
    if (!conn) { this.abandon(''); return; }
    // Past the server's 25 s window a "landed" is refused quietly: never toast a fish the server did not give.
    const late = performance.now() > this.castSeenMs + this.biteAfterMs + BITE_WINDOW_MS - 1000;
    try {
      await conn.reducers.reelIn({ landed: landed && !late });
      if (!landed || late) { this.abandon(late ? 'The line went slack — it got away' : 'It got away'); return; }
      const c = fishSpec(species);
      const cast = this.myCastCm;
      this.lastResult = `landed ${species}`;
      bus.emit('atlas:toast', { text: `You landed a ${c?.name ?? species}${cast ? ` — ${Math.round(cast)} cm` : ''}`, kind: 'success', ms: 4000 });
      await this.showFish(species, cast);
    } catch (e) {
      this.abandon(String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, ''));
    }
  }
  private myCastCm = 0;
  private readonly rings = newBobberRings();
  /** Stamps the ripple field accepted (a disabled field — the flag off, minimal / potato — refuses them). */
  private ringsRung = 0;
  private ring(list: RingStamp[]): void { for (const r of list) if (stampWater(r.x, r.z, r.strength, r.radius)) this.ringsRung++; }

  private abandon(msg: string): void {
    this.phase = 'idle';
    this.reel = null;
    this.holding = false;
    this.spot = null;
    this.hideReel();
    this.clearBobber();
    if (msg) { this.lastResult = msg; bus.emit('atlas:toast', { text: msg, kind: msg.startsWith('Reeled') ? 'info' : 'error', ms: 3000 }); }
  }

  // ── drawing ────────────────────────────────────────────────────────────────
  private drawBobber(s: CastSpot): void {
    this.clearBobber();
    const g = new THREE.Group();
    const top = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0xd8342a, roughness: 0.5 }));
    const bot = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0xf2efe6, roughness: 0.5 }));
    g.add(top, bot);
    const y = waterLiveAt(s.x, s.z) ?? waterStillAt(s.x, s.z) ?? this.seaY(s);
    g.position.set(s.x, y, s.z);
    this.scene.add(g);
    this.bobber = g;
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    geo.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0], 3)); // THE MISSING-ATTRIBUTE LAW (MRT)
    this.line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0xdcdcd4, transparent: true, opacity: 0.7 }));
    this.line.frustumCulled = false;
    this.scene.add(this.line);
  }

  private bob(now: number, bitten: boolean): void {
    const b = this.bobber, s = this.spot;
    if (!b || !s) return;
    const surf = waterLiveAt(s.x, s.z) ?? waterStillAt(s.x, s.z) ?? this.seaY(s);
    const t = now / 1000;
    b.position.y = surf + (bitten ? -0.06 - Math.abs(Math.sin(t * 9)) * 0.08 : Math.sin(t * 2.2) * 0.02);
    // C7: reeling DRAWS the bobber in toward the angler by the reel's progress (the wake models travel that now exists); the
    // fish's lateral wiggle is a visual offset only — the rings read the un-jittered track, so the wiggle stops stamping wakes.
    let rx = s.x, rz = s.z;
    if (this.phase === 'reeling' && this.reel) {
      const k = Math.max(0, Math.min(1, this.reel.progress)) * 0.8;
      rx = s.x + (atlas.localX - s.x) * k; rz = s.z + (atlas.localZ - s.z) * k;
      b.position.x = rx + (this.reel.fish - 0.5) * 0.8;
      b.position.z = rz;
    }
    // THE BOBBER RINGS THE WATER (lib/bobber_rings.ts): faint rings while it floats, a deeper dip on the bite and tugs while
    // the fish pulls, a wake while it is reeled — on the shared ripple field every other body already stamps.
    this.ring(bobberRings(this.rings, this.phase === 'reeling' ? 'reel' : bitten ? 'bite' : 'float', now / 1000, rx, rz));
    const l = this.line;
    if (l) {
      const pos = l.geometry.getAttribute('position') as THREE.BufferAttribute;
      const fx = -Math.sin(atlas.localYaw), fz = -Math.cos(atlas.localYaw);
      pos.setXYZ(0, atlas.localX + fx * 0.5 + fz * 0.25, atlas.localY + 1.55, atlas.localZ + fz * 0.5 - fx * 0.25);
      pos.setXYZ(1, b.position.x, b.position.y + 0.07, b.position.z);
      pos.needsUpdate = true;
    }
    const c = this.myCast();
    if (c) this.myCastCm = c.cm;
  }

  /** The sea's level where the rendered water has not arrived yet (the analytic fallback), else the feet. */
  private seaY(s: CastSpot): number {
    const seed = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? commonsSeedNow() : '';
    return s.water === 'sea' && seed ? commonsSeaLevel(seed) : atlas.localY;
  }

  private clearBobber(): void {
    for (const o of [this.bobber, this.line]) {
      if (!o) continue;
      o.removeFromParent();
      o.traverse((m) => { const mm = m as THREE.Mesh; mm.geometry?.dispose(); (mm.material as THREE.Material | undefined)?.dispose?.(); });
    }
    this.bobber = null;
    this.line = null;
  }

  private drawReel(): void {
    const s = this.reel;
    if (!s) return;
    if (!this.reelUi) {
      const root = el('div', 'position:fixed;right:22%;top:30%;width:48px;height:300px;z-index:42;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 88%, transparent);pointer-events:none');
      root.id = 'fishing-reel';
      const zone = el('div', `position:absolute;left:4px;right:14px;height:${REEL.ZONE_HALF * 2 * 100}%;background:color-mix(in srgb, var(--ax-good) 55%, transparent);border-radius:var(--ax-radius)`);
      const fish = el('div', 'position:absolute;left:10px;width:18px;height:10px;margin-top:-5px;background:var(--ax-accent);border-radius:50%');
      const meter = el('div', 'position:absolute;right:3px;bottom:3px;width:7px;background:var(--ax-info);border-radius:var(--ax-radius)');
      const label = el('div', 'position:absolute;top:-24px;left:50%;transform:translateX(-50%);white-space:nowrap;font:600 12px var(--ax-font);color:var(--ax-text)', `Hold ${interactKey()}`);
      root.append(zone, fish, meter, label);
      document.body.appendChild(root);
      this.reelUi = { root, zone, fish, meter, label };
    }
    const u = this.reelUi;
    u.zone.style.bottom = `${(s.zone - REEL.ZONE_HALF) * 100}%`;
    u.fish.style.bottom = `${s.fish * 100}%`;
    u.meter.style.height = `calc(${s.progress * 100}% - 6px)`;
  }
  private hideReel(): void { this.reelUi?.root.remove(); this.reelUi = null; }

  private model(key: string): Promise<THREE.Object3D | null> {
    const have = this.models.get(key);
    if (have) return Promise.resolve(have);
    let p = this.modelLoads.get(key);
    if (!p) {
      const assets = this.app.getModule('AssetModule') as { loadUrl?(url: string): Promise<THREE.Object3D> } | undefined;
      p = (async () => {
        if (!assets?.loadUrl) return null;
        try {
          const obj = await assets.loadUrl(new URL(`${FISH_DIR}/${key}.glb`, document.baseURI).toString());
          obj.traverse((o) => { o.userData.sharedAsset = true; });
          this.models.set(key, obj);
          return obj;
        } catch (e) { Logger.warn('Fishing', `${key}: ${String((e as Error)?.message ?? e)}`); return null; }
      })();
      this.modelLoads.set(key, p);
    }
    return p;
  }

  /** Hold the landed fish up in front of the camera for a moment, at its true length, side-on. */
  private async showFish(key: string, cm: number): Promise<void> {
    const master = await this.model(key);
    this.clearBobber();
    if (!master) { this.phase = 'idle'; return; }
    const holder = new THREE.Group();
    const inner = master.clone(true);
    inner.traverse((o) => { o.userData.sharedAsset = true; });
    const box = new THREE.Box3().setFromObject(inner);
    const size = box.getSize(new THREE.Vector3());
    const len = Math.max(size.x, size.z) || 1;
    const scale = fishDrawLength(cm || 30) / len;
    const centre = box.getCenter(new THREE.Vector3());
    inner.position.set(-centre.x * scale, -centre.y * scale, -centre.z * scale);
    inner.scale.setScalar(scale);
    const turn = new THREE.Group();
    turn.add(inner);
    // Head to the viewer's left, side-on: forward (+Z after fishModelYaw) turned across the view.
    turn.rotation.y = fishModelYaw(key);
    holder.add(turn);
    const fx = -Math.sin(atlas.localYaw), fz = -Math.cos(atlas.localYaw);
    const d = 1.1 + fishDrawLength(cm || 30) * 0.5;
    holder.position.set(atlas.localX + fx * d, atlas.localY + 1.45, atlas.localZ + fz * d);
    holder.rotation.y = atlas.localYaw + Math.PI / 2;
    this.scene.add(holder);
    this.shown = holder;
    this.showUntil = performance.now() + SHOW_MS;
  }

  private animateShown(now: number): void {
    const h = this.shown;
    if (!h) return;
    h.children[0].rotation.z = Math.sin(now / 120) * 0.12; // a flapping catch
    if (now < this.showUntil) return;
    h.removeFromParent();
    this.shown = null;
    if (this.phase === 'showing') this.phase = 'idle';
  }

  private showPrompt(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const show = text !== null;
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (show && p.textContent !== text) p.textContent = text;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.offInteract?.(); this.offInteract = null;
    this.abandon('');
    this.prompt?.remove();
  }
}
